import React, { useState, useEffect, useCallback } from 'react';
import { Receipt, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAYMENT_METHODS = ['Cash', 'PayNow', 'Cheque', 'Bank Transfer', 'Other'];

/**
 * Reusable modal for recording a customer payment against an invoice.
 * Props:
 *  - open, onOpenChange
 *  - invoice          : optional invoice object (auto-fills amount, customer, reference)
 *  - invoiceSource    : 'vehicle' | 'warehouse'
 *  - onCreated        : callback(pv) after success
 */
const RecordPaymentDialog = ({ open, onOpenChange, invoice = null, invoiceSource = 'vehicle', onCreated }) => {
  const today = new Date().toISOString().slice(0, 10);

  const init = useCallback(() => ({
    customer_name: invoice?.customer_name || '',
    customer_phone: invoice?.customer_phone || '',
    payment_date: today,
    amount: invoice?.total || invoice?.total_amount || '',
    payment_method: 'Cash',
    cheque_no: '',
    received_by: '',
    invoice_id: invoice?.id || '',
    invoice_number: invoice?.invoice_number || invoice?.document_number || '',
    invoice_source: invoiceSource,
    reference: invoice?.invoice_number || invoice?.document_number || '',
    description: (invoice?.line_items || []).map(it => it.description).filter(Boolean).slice(0, 2).join(', ') || '',
    remarks: '',
  }), [invoice, invoiceSource, today]);

  const [form, setForm] = useState(init);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(init());
  }, [open, init]);

  const save = async () => {
    if (!form.customer_name || !form.amount) {
      toast.error('Customer name and amount are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      const res = await axios.post(`${BACKEND_URL}/api/payment-vouchers`, payload);
      const pv = res.data.payment_voucher;
      toast.success(`Payment voucher ${pv.document_number} created`);
      // open the PDF in new tab
      window.open(`${BACKEND_URL}/api/payment-vouchers/${pv.id}/pdf`, '_blank');
      onCreated?.(pv);
      onOpenChange(false);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to create payment voucher'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt size={20} className="text-emerald-600" /> Record Customer Payment
          </DialogTitle>
          {invoice && (
            <p className="text-sm text-gray-600">
              For invoice <span className="font-mono">{form.invoice_number}</span>
            </p>
          )}
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
          <div className="md:col-span-2">
            <Label>Customer Name *</Label>
            <Input value={form.customer_name}
                   onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                   data-testid="pv-customer-name" />
          </div>

          <div>
            <Label>Payment Date *</Label>
            <Input type="date" value={form.payment_date}
                   onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                   data-testid="pv-date" />
          </div>
          <div>
            <Label>Amount (SGD) *</Label>
            <Input type="number" step="0.01" value={form.amount}
                   onChange={(e) => setForm({ ...form, amount: e.target.value })}
                   data-testid="pv-amount" />
          </div>

          <div>
            <Label>Payment Method *</Label>
            <Select value={form.payment_method}
                    onValueChange={(v) => setForm({ ...form, payment_method: v })}>
              <SelectTrigger data-testid="pv-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Cheque No. (if cheque)</Label>
            <Input value={form.cheque_no}
                   onChange={(e) => setForm({ ...form, cheque_no: e.target.value })}
                   disabled={form.payment_method !== 'Cheque'}
                   placeholder="e.g. 123456"
                   data-testid="pv-cheque-no" />
          </div>

          <div>
            <Label>Received By (NJK staff)</Label>
            <Input value={form.received_by}
                   onChange={(e) => setForm({ ...form, received_by: e.target.value })}
                   placeholder="Your name"
                   data-testid="pv-received-by" />
          </div>
          <div>
            <Label>Reference</Label>
            <Input value={form.reference}
                   onChange={(e) => setForm({ ...form, reference: e.target.value })}
                   placeholder="INV-2026-001 / DO-002"
                   data-testid="pv-reference" />
          </div>

          <div className="md:col-span-2">
            <Label>Being Payment For</Label>
            <Input value={form.description}
                   onChange={(e) => setForm({ ...form, description: e.target.value })}
                   placeholder="Lorry crane service / Warehouse storage / etc."
                   data-testid="pv-description" />
          </div>

          <div className="md:col-span-2">
            <Label>Remarks (optional)</Label>
            <Textarea value={form.remarks}
                      onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                      placeholder="Any extra notes..."
                      rows={2}
                      data-testid="pv-remarks" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pv-cancel-btn">
            <X size={16} className="mr-1" /> Cancel
          </Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pv-save-btn">
            <Receipt size={16} className="mr-2" /> {saving ? 'Saving...' : 'Create & Open PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecordPaymentDialog;
